<template>
    <div class="public-report-page">
        <!-- Header -->
        <div class="header-bar">
            <div class="public-header">
                <a href="https://formwise.io" target="_blank" class="navbar-brand">
                    <img src="@/assets/logo.svg" alt="FormWise">
                </a>
                <div class="header-actions">
                    <a href="https://app.formwise.io/login" class="btn btn-outline">Log In</a>
                    <a href="https://formwise.io/#register" class="btn btn-primary">Register</a>
                </div>
            </div>
        </div>

        <!-- Loading -->
        <div class="page-wrapper" v-if="loading">
            <div class="loading-state">
                <div class="spinner-border text-primary"></div>
                <p>Loading report...</p>
            </div>
        </div>

        <!-- Error -->
        <div class="page-wrapper" v-else-if="error">
            <div class="error-state">
                <h2>Report Not Found</h2>
                <p>This report link is invalid or the report is no longer available.</p>
                <a href="https://formwise.io" class="btn btn-primary">Go to FormWise</a>
            </div>
        </div>

        <!-- Report Content -->
        <div class="page-wrapper" v-else>
            <div class="report-content">
                <!-- Title -->
                <h1>{{ report.name }}</h1>

                <!-- Info Grid -->
                <div class="info-grid">
                    <div class="info-col">
                        <p><strong>Project:</strong> {{ projectName }}</p>
                        <p><strong>Form:</strong> {{ formName }}</p>
                        <p><strong>Generated:</strong> {{ formatDate(report.created_at) }}</p>
                    </div>
                    <div class="info-col">
                        <p><strong>Submissions Analyzed:</strong> {{ result.summary.filtered_submissions }}</p>
                        <p><strong>Unique Values:</strong> {{ result.summary.unique_values }}</p>
                        <p><strong>Period:</strong> {{ result.summary.period_label }}</p>
                    </div>
                </div>

                <!-- Bar Chart -->
                <div class="chart-section" v-if="result.breakdown.length > 0">
                    <h2 class="section-title">Value Distribution</h2>
                    <VueApexCharts type="bar" height="280" :options="barChartOptions" :series="barSeries"></VueApexCharts>
                </div>

                <!-- Line Chart -->
                <div class="chart-section" v-if="result.trends.labels.length > 1">
                    <h2 class="section-title">Trends Over Time</h2>
                    <VueApexCharts type="line" height="280" :options="lineChartOptions" :series="lineSeries"></VueApexCharts>
                </div>

                <!-- Breakdown Table -->
                <div class="breakdown-section" v-if="result.breakdown.length > 0">
                    <h2 class="section-title">Breakdown Table</h2>
                    <table class="table breakdown-table">
                        <thead>
                            <tr>
                                <th>Value</th>
                                <th>Count</th>
                                <th>Percentage</th>
                                <th>Distribution</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, idx) in result.breakdown" :key="idx">
                                <td>{{ item.value }}</td>
                                <td>{{ item.count }}</td>
                                <td>{{ item.percentage }}%</td>
                                <td>
                                    <div class="bar-wrapper">
                                        <div class="bar-fill" :style="{ width: item.percentage + '%' }"></div>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Footer -->
                <div class="report-footer">
                    <p>Generated by <a href="https://formwise.io" target="_blank">FormWise</a> - formwise.io</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from "axios";
import VueApexCharts from "vue-apexcharts";
import { API_URL } from "@/repository/repository";

const CHART_COLORS = [
    '#031FEB', '#FF331F', '#22C55E', '#F59E0B', '#8B5CF6',
    '#EC4899', '#06B6D4', '#F97316', '#6366F1', '#14B8A6',
    '#EF4444', '#84CC16', '#A855F7', '#3B82F6', '#10B981',
    '#E11D48', '#0EA5E9', '#D946EF', '#F43F5E', '#64748B',
];

export default {
    name: "PublicReport",
    components: { VueApexCharts },
    data() {
        return {
            loading: true,
            error: false,
            report: null,
            formName: '',
            projectName: '',
        }
    },
    created() {
        this.fetchReport();
    },
    methods: {
        fetchReport() {
            const token = this.$route.params.token;
            axios.get(API_URL + '/reports/public/' + token)
                .then(response => {
                    if (response.data.status === 'success') {
                        this.report = response.data.report;
                        this.formName = response.data.form_name;
                        this.projectName = response.data.project_name;
                    } else {
                        this.error = true;
                    }
                    this.loading = false;
                })
                .catch(() => {
                    this.error = true;
                    this.loading = false;
                });
        },
        formatDate(date) {
            if (!date) return '';
            return new Date(date).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
            });
        },
    },
    computed: {
        result() {
            return this.report ? this.report.result : null;
        },
        barSeries() {
            return [{
                name: 'Count',
                data: this.result.breakdown.map(item => item.count),
            }];
        },
        barChartOptions() {
            return {
                chart: {
                    type: 'bar',
                    toolbar: { show: false },
                },
                plotOptions: {
                    bar: {
                        columnWidth: '60%',
                        borderRadius: 6,
                        distributed: true,
                    },
                },
                colors: CHART_COLORS.slice(0, this.result.breakdown.length),
                dataLabels: { enabled: false },
                legend: { show: false },
                xaxis: {
                    categories: this.result.breakdown.map(item => item.value),
                    labels: {
                        style: { fontSize: '11px' },
                        trim: true,
                        maxHeight: 80,
                    },
                },
                yaxis: {
                    labels: {
                        formatter: (val) => Math.round(val),
                    },
                },
                grid: { borderColor: '#eee' },
                tooltip: {
                    y: {
                        formatter: (val, { dataPointIndex }) => {
                            const item = this.result.breakdown[dataPointIndex];
                            return val + ' (' + item.percentage + '%)';
                        },
                    },
                },
            };
        },
        lineSeries() {
            return this.result.trends.datasets.map((ds, idx) => ({
                name: ds.value,
                data: ds.data,
                color: CHART_COLORS[idx % CHART_COLORS.length],
            }));
        },
        lineChartOptions() {
            return {
                chart: {
                    type: 'line',
                    toolbar: { show: false },
                },
                stroke: {
                    curve: 'smooth',
                    width: 2,
                },
                xaxis: {
                    categories: this.result.trends.labels,
                    labels: {
                        formatter: (val) => {
                            if (!val) return '';
                            const parts = val.split('-');
                            if (parts.length === 2) {
                                const date = new Date(parts[0], parts[1] - 1);
                                return date.toLocaleString('default', { month: 'short', year: '2-digit' });
                            }
                            return val;
                        },
                    },
                },
                yaxis: {
                    labels: {
                        formatter: (val) => Math.round(val),
                    },
                },
                dataLabels: { enabled: false },
                grid: { borderColor: '#eee' },
                legend: {
                    position: 'bottom',
                    fontSize: '12px',
                },
            };
        },
    },
    metaInfo() {
        return {
            title: this.report ? this.report.name : 'Report',
            titleTemplate: '%s / FormWise Report',
        };
    },
}
</script>

<style lang="scss" scoped>
@import "../scss/variables";

.public-report-page {
    min-height: 100vh;
    background: $white;
    font-family: 'Inter', sans-serif;
}

.header-bar {
}

.public-header {
    max-width: 900px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.5rem;

    @include smartphone {
        padding: 0.75rem 15px;
    }

    .navbar-brand {
        display: inline-flex;
        align-items: center;
        text-decoration: none;

        img {
            height: 2.5rem;
            display: block;
        }
    }

    .header-actions {
        display: flex;
        align-items: center;
        gap: 8px;

        .btn-outline {
            background: transparent;
            border: 1px solid $border-grey;
            color: $dark;
            font-weight: 600;
            padding: 8px 20px;
            border-radius: $box-border-radius;
            text-decoration: none;
            font-size: 0.9rem;
            @extend .animated;

            &:hover {
                background: $hover-grey;
                border-color: $border-dark-grey;
            }
        }

        .btn-primary {
            font-size: 0.9rem;
            text-decoration: none;
        }
    }
}

.page-wrapper {
    padding: 2.5rem 1.5rem;
    max-width: 900px;
    margin: 0 auto;

    @include smartphone {
        padding: 1.5rem 15px;
    }
}

.report-content {
    h1 {
        font-weight: 700;
        font-size: 1.75rem;
        color: $dark;
        margin: 0 0 1.5rem 0;
    }
}

.loading-state,
.error-state {
    text-align: center;
    padding: 80px 20px;

    .spinner-border {
        width: 40px;
        height: 40px;
        margin-bottom: 16px;
    }

    p {
        color: $grey-text;
        font-size: 1rem;
    }

    h2 {
        font-size: 1.25rem;
        font-weight: 600;
        color: $dark;
        margin-bottom: 8px;
    }

    .btn {
        margin-top: 20px;
    }
}

.info-grid {
    display: flex;
    gap: 40px;
    margin-bottom: 2rem;

    @include smartphone {
        flex-direction: column;
        gap: 12px;
    }

    .info-col {
        flex: 1;

        p {
            margin-bottom: 4px;
            font-size: 0.9rem;
            color: $grey-text;

            strong {
                color: $dark;
                font-weight: 600;
            }
        }
    }
}

.chart-section {
    margin-bottom: 2rem;
}

.section-title {
    font-size: 1rem;
    font-weight: 600;
    color: $dark;
    margin-bottom: 0.75rem;
}

.breakdown-section {
    margin-bottom: 2rem;
}

.breakdown-table {
    font-size: 0.85rem;

    th {
        font-weight: 600;
        color: $grey-text;
        border-top: none;
        padding: 0.5rem;
    }

    td {
        padding: 0.5rem;
        vertical-align: middle;
    }

    .bar-wrapper {
        background: $hover-grey;
        border-radius: 10px;
        height: 16px;
        overflow: visible;

        .bar-fill {
            background: $primary;
            height: 100%;
            border-radius: 10px;
            min-width: 2px;
            box-shadow: 0 2px 6px rgba($primary, 0.4);
        }
    }
}

.report-footer {
    border-top: 1px solid $border-grey;
    padding-top: 1.5rem;
    margin-top: 1rem;

    p {
        margin: 0;
        color: $grey-text;
        font-size: 0.85rem;

        a {
            color: $primary;
            font-weight: 600;
            text-decoration: none;

            &:hover {
                text-decoration: underline;
            }
        }
    }
}
</style>
